import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, CheckCircle2, Ban, Pencil, Archive, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

const ICON = { approved: CheckCircle2, rejected: Ban, changes_requested: Pencil, archived: Archive };
const COLOR = {
  approved: "text-emerald-400", rejected: "text-red-400",
  changes_requested: "text-orange-400", archived: "text-slate-400",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markOne = async (n) => {
    if (n.read) return;
    setItems((s) => s.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${n.id}/read`); } catch { load(); }
  };

  const markAll = async () => {
    setItems((s) => s.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch { load(); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading text-sm font-bold">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notification-mark-all" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notification-empty">You're all caught up.</div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((n) => {
                const Icon = ICON[n.status] || Bell;
                return (
                  <button key={n.id} onClick={() => markOne(n)} data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-secondary/60 transition-colors ${n.read ? "opacity-70" : "bg-primary/5"}`}>
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${COLOR[n.status] || "text-muted-foreground"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm leading-snug text-foreground">{n.title}</div>
                      {n.body && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="text-[11px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
